import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"

/**
 * User-entered fields rendered as a labelled key/value table instead of
 * raw JSON. Labels reuse the same i18n keys the feedback dialog uses for
 * its form labels so admins see the same wording the user saw while
 * submitting.
 */
const FIELD_LABEL_KEYS: Record<string, { key: string; fallback: string }> = {
  sentiment: { key: "feedback.responseFeedback.sentimentLabel", fallback: "Sentiment" },
  comment: { key: "feedback.responseFeedback.commentLabel", fallback: "Comment" },
  whatTrying: { key: "feedback.bug.whatTryingLabel", fallback: "What were you trying to do?" },
  expected: { key: "feedback.bug.expectedLabel", fallback: "Expected" },
  happened: { key: "feedback.bug.happenedLabel", fallback: "What happened" },
  errorText: { key: "feedback.bug.errorTextLabel", fallback: "Error text" },
  reproducibility: { key: "feedback.bug.reproducibilityLabel", fallback: "Reproducibility" },
  whoAffected: { key: "feedback.bug.whoAffectedLabel", fallback: "Who is affected" },
  whoAffectedOther: {
    key: "adminDashboard.feedback.detail.whoAffectedOther",
    fallback: "Other (specify)",
  },
  severity: { key: "feedback.bug.severityLabel", fallback: "Severity" },
  whenStarted: { key: "feedback.bug.whenStartedLabel", fallback: "When did it start" },
  pageFeature: { key: "feedback.bug.pageFeatureLabel", fallback: "Where in the app" },
  summary: { key: "feedback.incident.summaryLabel", fallback: "Summary" },
  impact: { key: "feedback.incident.impactLabel", fallback: "Impact" },
  timeframe: { key: "feedback.incident.timeframeLabel", fallback: "Timeframe" },
}

// Blob paths — not useful inline; the images already render below.
const SKIP_KEYS = new Set(["evidenceBlobPath", "screenshotBlobPath"])

// Typically long — render full-width, preserving newlines.
const LONG_TEXT_KEYS = new Set([
  "comment",
  "whatTrying",
  "expected",
  "happened",
  "errorText",
  "summary",
  "impact",
])

export function FieldsTable({ fields }: { fields: Record<string, unknown> }) {
  const { t } = useTranslation()
  const entries = Object.entries(fields).filter(([key, value]) => {
    if (SKIP_KEYS.has(key)) return false
    if (value === null || value === undefined) return false
    if (typeof value === "string" && value.trim() === "") return false
    return true
  })

  if (entries.length === 0) {
    return (
      <p className="text-muted-foreground text-xs italic">
        {t("adminDashboard.feedback.detail.fieldsEmpty", "No fields were submitted.")}
      </p>
    )
  }

  return (
    <dl className="divide-border bg-muted/30 border-border grid divide-y rounded-md border text-xs">
      {entries.map(([key, value]) => {
        const label = Object.prototype.hasOwnProperty.call(FIELD_LABEL_KEYS, key)
          ? FIELD_LABEL_KEYS[key]
          : undefined
        const labelText = label ? t(label.key, label.fallback) : humaniseKey(key)
        const isLong = LONG_TEXT_KEYS.has(key)
        return (
          <div
            key={key}
            className={cn(
              "grid gap-1 p-3 sm:gap-4",
              isLong ? "sm:grid-cols-1" : "sm:grid-cols-[10rem_1fr] sm:items-start"
            )}
          >
            <dt className="text-muted-foreground font-medium">{labelText}</dt>
            <dd className={isLong ? "whitespace-pre-wrap" : "min-w-0 break-words"}>
              {renderValue(value)}
            </dd>
          </div>
        )
      })}
    </dl>
  )
}

function renderValue(value: unknown): React.ReactNode {
  if (typeof value === "string") return value
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  return (
    <code className="text-muted-foreground font-mono text-[11px]">{JSON.stringify(value)}</code>
  )
}

/** "whoAffected" → "Who affected"; only used as fallback for unknown keys. */
function humaniseKey(key: string): string {
  const spaced = key.replace(/([A-Z])/g, " $1").replace(/_/g, " ")
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}
